import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Notification,
  NotificationService,
} from '../../core/notification.service';

/**
 * NotificationPanelComponent displays a slide-out panel with tenant notifications.
 *
 * Features:
 * - Slides from right side with backdrop overlay
 * - Reverse-chronological notification list
 * - Mark as read on click
 * - Link to affected bookings via metadata
 * - Cursor-based pagination (load more)
 * - Dark mode support
 *
 * Requirements: 3.4, 3.5
 */
@Component({
    selector: 'app-notification-panel',
    imports: [CommonModule, TranslateModule, RouterModule],
    template: `
    @if (isOpen) {
      <!-- Backdrop -->
      <div
        class="fixed inset-0 bg-black/40 z-40 transition-opacity"
        (click)="close()"
        aria-hidden="true"
      ></div>

      <!-- Panel -->
      <aside
        class="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-xl z-50
               transform transition-transform duration-300 ease-in-out flex flex-col"
        [class.translate-x-0]="isOpen"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="'notifications.title' | translate"
      >
        <!-- Header -->
        <header class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {{ 'notifications.title' | translate }}
          </h2>
          <button
            type="button"
            class="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
            [attr.aria-label]="'common.close' | translate"
            (click)="close()"
          >
            ✕
          </button>
        </header>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-5 py-4">
          @if (loading() && notifications().length === 0) {
            <div class="text-center py-12 text-gray-500 dark:text-gray-400">
              <p>{{ 'common.loading' | translate }}</p>
            </div>
          } @else if (notifications().length === 0) {
            <div class="text-center py-12 text-gray-500 dark:text-gray-400">
              <p>{{ 'notifications.empty' | translate }}</p>
            </div>
          } @else {
            <ul class="list-none p-0 m-0 space-y-3">
              @for (notification of notifications(); track notification.notification_id) {
                <li
                  class="p-4 rounded-lg border cursor-pointer transition-colors"
                  [class]="notification.read
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'"
                  (click)="onNotificationClick(notification)"
                >
                  <div class="flex items-start gap-3">
                    <!-- Unread indicator -->
                    <span
                      class="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                      [class]="notification.read
                        ? 'bg-transparent'
                        : 'bg-blue-500'"
                      [attr.aria-label]="notification.read ? null : ('notifications.unread' | translate)"
                    ></span>

                    <div class="flex-1 min-w-0">
                      <!-- Title -->
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {{ notification.title }}
                      </p>

                      <!-- Body -->
                      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {{ notification.body }}
                      </p>

                      <!-- Timestamp -->
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {{ formatTimestamp(notification.created_at) }}
                      </p>

                      <!-- Booking links -->
                      @if (notification.metadata?.affected_booking_ids?.length) {
                        <div class="mt-2 flex flex-wrap gap-1">
                          @for (bookingId of notification.metadata!.affected_booking_ids!; track bookingId) {
                            <a
                              class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              [routerLink]="['/bookings']"
                              [queryParams]="{ booking_id: bookingId }"
                              (click)="$event.stopPropagation()"
                            >
                              {{ 'notifications.viewBooking' | translate }}
                            </a>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </li>
              }
            </ul>

            <!-- Load more -->
            @if (lastKey()) {
              <div class="mt-4 text-center">
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400
                         hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500
                         disabled:opacity-50"
                  [disabled]="loadingMore()"
                  (click)="loadMore()"
                >
                  @if (loadingMore()) {
                    {{ 'common.loading' | translate }}
                  } @else {
                    {{ 'notifications.loadMore' | translate }}
                  }
                </button>
              </div>
            }
          }
        </div>
      </aside>
    }
  `,
    styles: [`
    :host {
      display: block;
    }

    /* Dark mode host-level styles for custom scrollbar and overlay */
    :host-context(.dark) ::-webkit-scrollbar {
      width: 6px;
    }

    :host-context(.dark) ::-webkit-scrollbar-track {
      background: #1f2937;
    }

    :host-context(.dark) ::-webkit-scrollbar-thumb {
      background: #4b5563;
      border-radius: 3px;
    }

    :host-context(.dark) ::-webkit-scrollbar-thumb:hover {
      background: #6b7280;
    }
  `]
})
export class NotificationPanelComponent implements OnChanges {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** Controls panel visibility. */
  @Input() isOpen = false;

  /** Emits when the panel should be closed. */
  @Output() closed = new EventEmitter<void>();

  /** Local state signals. */
  readonly notifications = signal<Notification[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly lastKey = signal<string | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fetchNotifications();
    }
  }

  /** Fetch first page of notifications. */
  fetchNotifications(): void {
    this.loading.set(true);
    this.notificationService.getNotifications().subscribe({
      next: (response) => {
        if (response) {
          this.notifications.set(response.notifications);
          this.lastKey.set(response.last_key);
        }
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  /** Load next page using cursor-based pagination. */
  loadMore(): void {
    const cursor = this.lastKey();
    if (!cursor) return;

    this.loadingMore.set(true);
    this.notificationService.getNotifications(cursor).subscribe({
      next: (response) => {
        if (response) {
          this.notifications.update((current) => [
            ...current,
            ...response.notifications,
          ]);
          this.lastKey.set(response.last_key);
        }
        this.loadingMore.set(false);
      },
      error: () => {
        this.loadingMore.set(false);
      },
    });
  }

  /** Mark notification as read on click and navigate if applicable. */
  onNotificationClick(notification: Notification): void {
    if (!notification.read) {
      this.notificationService.markAsRead(notification.notification_id).subscribe({
        next: () => {
          this.notifications.update((list) =>
            list.map((n) =>
              n.notification_id === notification.notification_id
                ? { ...n, read: true }
                : n
            )
          );
        },
      });
    }

    // Navigate to affected bookings if metadata present
    if (notification.metadata?.affected_booking_ids?.length) {
      this.router.navigate(['/bookings'], {
        queryParams: {
          booking_id: notification.metadata.affected_booking_ids[0],
        },
      });
      this.close();
    }
  }

  /** Close the panel. */
  close(): void {
    this.closed.emit();
  }

  /** Format ISO timestamp to a user-friendly relative/absolute string. */
  formatTimestamp(isoString: string): string {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60_000);
      const diffHours = Math.floor(diffMs / 3_600_000);
      const diffDays = Math.floor(diffMs / 86_400_000);

      if (diffMin < 1) return this.translate.instant('notifications.time.justNow');
      if (diffMin < 60) return this.translate.instant('notifications.time.minutesAgo', { count: diffMin });
      if (diffHours < 24) return this.translate.instant('notifications.time.hoursAgo', { count: diffHours });
      if (diffDays < 7) return this.translate.instant('notifications.time.daysAgo', { count: diffDays });

      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  }
}
